import { cn } from "@/utils";
import type { LucideIcon } from "lucide-react";
import React from "react";

export interface RailRowProps
  extends Omit<React.ComponentPropsWithoutRef<"button">, "children"> {
  icon: LucideIcon;
  label: string;
  meta?: string;
  active?: boolean;
  leading?: React.ReactNode;
  trailing?: React.ReactNode;
  /**
   * Optional second line under the label (e.g. a breadcrumb in search
   * results). The row grows to two lines; the icon top-aligns to the label.
   */
  subLabel?: React.ReactNode;
}

/** Leaf rail row — comp `Row` styling. */
export const RailRow = React.forwardRef<HTMLButtonElement, RailRowProps>(
  function RailRow(
    {
      icon: Icon,
      label,
      meta,
      active,
      leading,
      trailing,
      subLabel,
      className,
      type = "button",
      ...props
    },
    ref,
  ) {
    return (
      <button
        ref={ref}
        type={type}
        className={cn(
          "flex min-h-7 w-full gap-2.5 rounded-md px-2 text-left hover:bg-n-05",
          subLabel ? "items-start py-1" : "items-center",
          active && "bg-n-05 text-n-90",
          className,
        )}
        {...props}
      >
        {leading ?? (
          <Icon
            className={cn("size-4 shrink-0 text-n-50", subLabel && "mt-[3px]")}
          />
        )}
        <span className="flex min-w-0 flex-1 flex-col">
          <span className="flex min-w-0 items-center gap-1 truncate text-base text-n-80">
            <span className="truncate">{label}</span>
            {trailing}
          </span>
          {subLabel && (
            <span className="truncate text-xs text-n-40">{subLabel}</span>
          )}
        </span>
        {meta && (
          <span className="shrink-0 font-mono text-2xs text-n-40">{meta}</span>
        )}
      </button>
    );
  },
);
